<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog/index.js'
  import { Button } from '$lib/components/ui/button/index.js'
  import { Input } from '$lib/components/ui/input/index.js'
  import { listSeriesMatches, fixSeriesMatch } from '$lib/apis/series/api'
  import { trackOperation, operationTitle } from '$lib/operations.svelte'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'

  type FuzzyDate = {
    year?: number
    month?: number
    day?: number
  }

  type Match = {
    title?: string
    altTitles?: string[]
    matchScore?: number
    startDate?: FuzzyDate
    mediaIds?: Record<string, unknown>
    staff?: { name?: string; role?: string }[]
  }

  let {
    open = $bindable(false),
    server,
    seriesId,
    localTitle = '',
  }: {
    open?: boolean
    server: any
    seriesId: string
    localTitle?: string
  } = $props()

  let query = $state('')
  let matches = $state<Match[]>([])
  let loading = $state(false)
  let submitting = $state(false)
  let error = $state<string | null>(null)
  let selectedIndex = $state<number | null>(null)

  function scoreLabel(score?: number) {
    if (score == null || Number.isNaN(score)) return '—'
    return `${Math.round(score * 100)}%`
  }

  function startDateLabel(d?: FuzzyDate) {
    if (!d?.year) return ''
    const parts = [String(d.year)]
    if (d.month) parts.push(String(d.month).padStart(2, '0'))
    if (d.day) parts.push(String(d.day).padStart(2, '0'))
    return parts.join('-')
  }

  function providerHints(m: Match) {
    const ids = m.mediaIds ?? {}
    const parts: string[] = []
    if (ids.anilist) parts.push(`AL ${ids.anilist}`)
    if (ids.mal) parts.push(`MAL ${ids.mal}`)
    if (ids.ranobedb) parts.push(`RDB ${ids.ranobedb}`)
    if (ids.novelUpdate) parts.push(`NU ${ids.novelUpdate}`)
    if (ids.mangaUpdate) parts.push(`MU ${ids.mangaUpdate}`)
    if (ids.bookwalker) parts.push(`BW ${ids.bookwalker}`)
    return parts.join(' · ')
  }

  async function loadMatches(q?: string) {
    loading = true
    error = null
    selectedIndex = null
    try {
      const data = await listSeriesMatches(server, seriesId, q)
      matches = data?.matches ?? []
    } catch (err) {
      console.error('list matches failed', err)
      error = 'Could not load matches.'
      matches = []
    } finally {
      loading = false
    }
  }

  $effect(() => {
    if (!open) {
      query = ''
      matches = []
      loading = false
      submitting = false
      error = null
      selectedIndex = null
      return
    }
    query = localTitle
    void loadMatches()
  })

  async function onSearch(e?: Event) {
    e?.preventDefault()
    await loadMatches(query)
  }

  async function onConfirm() {
    if (selectedIndex == null || submitting) return
    const match = matches[selectedIndex]
    if (!match) return
    submitting = true
    error = null
    try {
      const data = await fixSeriesMatch(server, seriesId, match)
      trackOperation({
        server,
        operation: data?.operation ?? {},
        title: operationTitle(data?.operation?.type, 'Fixing match'),
        description: match.title ?? localTitle,
      })
      open = false
    } catch (err) {
      console.error('fix match failed', err)
      error =
        err instanceof Error && err.message
          ? err.message
          : 'Could not apply match.'
    } finally {
      submitting = false
    }
  }
</script>

<Dialog.Root bind:open>
  <Dialog.Content
    class="flex max-h-[min(90dvh,calc(100vh-2rem))] flex-col gap-4
      border-latte-surface1/60 bg-latte-mantle/95 sm:max-w-lg
      dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
  >
    <Dialog.Header>
      <Dialog.Title class="text-latte-text dark:text-macchiato-text">
        Fix Match
      </Dialog.Title>
      <Dialog.Description class="text-latte-text/65 dark:text-macchiato-text/65">
        Choose the correct metadata match for
        <span class="font-medium text-latte-text dark:text-macchiato-text">
          {localTitle || 'this series'}
        </span>.
      </Dialog.Description>
    </Dialog.Header>

    <form class="flex gap-2" onsubmit={onSearch}>
      <Input
        bind:value={query}
        placeholder="Search title…"
        aria-label="Search matches"
        class="flex-1"
        disabled={submitting}
      />
      <Button type="submit" variant="outline" disabled={loading || submitting}>
        Search
      </Button>
    </form>

    <div
      class="min-h-48 max-h-72 overflow-y-auto rounded-md border border-latte-surface1/60
        bg-latte-base/50 text-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-base/50"
      role="listbox"
      aria-label="Match candidates"
      aria-busy={loading}
    >
      {#if loading}
        <p class="p-4 text-sm text-latte-text/60 dark:text-macchiato-text/60">
          Searching…
        </p>
      {:else if error && matches.length === 0}
        <div
          class="flex items-start gap-2 p-4 text-sm text-latte-red dark:text-macchiato-red"
          role="alert"
        >
          <AlertCircleIcon class="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
          <p class="min-w-0 leading-snug">{error}</p>
        </div>
      {:else if matches.length === 0}
        <p class="p-4 text-sm text-latte-text/60 dark:text-macchiato-text/60">
          No matches found.
        </p>
      {:else}
        {#each matches as match, i (i)}
          <button
            type="button"
            role="option"
            aria-selected={selectedIndex === i}
            class="flex w-full flex-col gap-0.5 border-b border-latte-surface1/50 px-3 py-2.5
              text-left last:border-b-0 hover:bg-latte-surface1/40
              dark:border-macchiato-surface1/50 dark:hover:bg-macchiato-surface1/40
              {selectedIndex === i
                ? 'bg-latte-surface1/55 dark:bg-macchiato-surface1/55'
                : ''}"
            disabled={submitting}
            onclick={() => (selectedIndex = i)}
          >
            <div class="flex items-baseline justify-between gap-3">
              <span
                class="min-w-0 flex-1 truncate font-medium text-latte-text
                  dark:text-macchiato-text"
              >
                {match.title ?? 'Untitled'}
              </span>
              <span
                class="shrink-0 text-xs tabular-nums text-latte-green
                  dark:text-macchiato-green"
              >
                {scoreLabel(match.matchScore)}
              </span>
            </div>
            <div
              class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs
                text-latte-text/55 dark:text-macchiato-text/55"
            >
              {#if startDateLabel(match.startDate)}
                <span class="tabular-nums">{startDateLabel(match.startDate)}</span>
              {/if}
              {#if startDateLabel(match.startDate) && providerHints(match)}
                <span aria-hidden="true">·</span>
              {/if}
              {#if providerHints(match)}
                <span class="truncate">{providerHints(match)}</span>
              {/if}
            </div>
          </button>
        {/each}
      {/if}
    </div>

    {#if error && matches.length > 0}
      <div
        class="flex items-start gap-2 rounded-md border border-latte-red/35
          bg-latte-red/8 px-3 py-2 text-sm text-latte-red
          dark:border-macchiato-red/35 dark:bg-macchiato-red/10
          dark:text-macchiato-red"
        role="alert"
      >
        <AlertCircleIcon class="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
        <p class="min-w-0 leading-snug">{error}</p>
      </div>
    {/if}

    <Dialog.Footer class="gap-2 sm:justify-end">
      <Button
        type="button"
        variant="outline"
        disabled={submitting}
        onclick={() => (open = false)}
      >
        Cancel
      </Button>
      <Button
        type="button"
        class="bg-latte-green text-white hover:bg-latte-green/90
          dark:bg-macchiato-green dark:text-macchiato-base
          dark:hover:bg-macchiato-green/90"
        disabled={selectedIndex == null || submitting || loading}
        onclick={onConfirm}
      >
        {submitting ? 'Applying…' : 'Apply'}
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
